import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { JournalAttachment } from "@/types/journal";
import { toast } from "sonner";

export const useJournalAttachments = (entryId: string | null) => {
  const [attachments, setAttachments] = useState<JournalAttachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const queryClient = useQueryClient();
  
  const { data: fetchedAttachments, isLoading } = useQuery({
    queryKey: ['journal-attachments', entryId],
    queryFn: async () => {
      if (!entryId) return [];
      
      try {
        const { data, error } = await supabase.rpc('get_journal_attachments', {
          entry_id: entryId
        });
        
        if (error) throw error;
        return data as JournalAttachment[];
      } catch (error: any) {
        console.error("Error fetching attachments:", error);
        toast.error("Failed to load attachments");
        return [];
      }
    },
    enabled: !!entryId
  });
  
  useEffect(() => {
    if (fetchedAttachments) {
      setAttachments(fetchedAttachments);
    }
  }, [fetchedAttachments]);
  
  const uploadAttachment = async (file: File, providedEntryId?: string): Promise<JournalAttachment | null> => {
    const targetEntryId = providedEntryId || entryId;
    
    if (!targetEntryId) {
      console.error("No entry ID available for upload");
      return null;
    }
    
    setIsUploading(true);
    
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("You must be logged in to upload files");
        setIsUploading(false); // Reset uploading state
        return null;
      }

      console.log("Starting file upload process for:", file.name);
      console.log("File details:", {
        size: file.size,
        type: file.type,
        lastModified: file.lastModified
      });

      // Create a unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;
      
      console.log("Generated file path:", filePath);

      // Upload file to storage
      console.log("Uploading to storage...");
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('journal_attachments')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error("Error uploading file:", uploadError);
        toast.error(`Error uploading file: ${uploadError.message}`);
        setIsUploading(false); // Reset uploading state
        return null;
      }

      console.log("File uploaded successfully to storage:", uploadData);

      // Create attachment record
      console.log("Creating attachment record in database...");
      const { data: attachment, error: attachmentError } = await supabase
        .from('journal_attachments')
        .insert({
          journal_entry_id: targetEntryId,
          filename: file.name,
          file_path: filePath,
          file_type: fileExt || '',
          file_size: file.size,
          mime_type: file.type,
          upload_status: 'validated',
          is_validated: true
        })
        .select('*')
        .single();

      if (attachmentError) {
        console.error("Error creating attachment:", attachmentError);
        toast.error(`Error creating attachment: ${attachmentError.message}`);
        setIsUploading(false); // Reset uploading state
        
        // Clean up the uploaded file if we couldn't create the attachment
        console.log("Cleaning up uploaded file due to database error...");
        await supabase.storage.from('journal_attachments').remove([filePath]);
        return null;
      }
      
      console.log("Attachment record created successfully:", attachment);
      
      // Add the attachment to our local state with validated status
      const newAttachment = {
        ...attachment,
        upload_status: 'validated',
        is_validated: true
      };
      
      setAttachments(prev => [...prev, newAttachment]);
      
      // Invalidate the query to trigger a refetch
      await queryClient.invalidateQueries({ queryKey: ['journal-attachments', targetEntryId] });
      await queryClient.invalidateQueries({ queryKey: ['journal-attachments'] });
      
      toast.success(`File "${file.name}" uploaded successfully`);
      
      return newAttachment;
    } catch (error: any) {
      console.error("Unexpected error during file upload:", error);
      toast.error(`Upload failed: ${error.message || 'Unknown error'}`);
      return null;
    } finally {
      setIsUploading(false); // Always reset the uploading state when done
    }
  };
  
  const addAttachment = (attachment: JournalAttachment) => {
    setAttachments(prev => [...prev, attachment]);
  };
  
  const removeAttachment = async (id: string) => {
    // Find the attachment to get its file path
    const attachment = attachments.find(a => a.id === id);
    
    if (!attachment) {
      console.error("Attachment not found for removal:", id);
      return;
    }
    
    try {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('journal_attachments')
        .remove([attachment.file_path]);
      
      if (storageError) {
        toast.error(`Error removing file: ${storageError.message}`);
        return;
      }
      
      // Delete from database
      const { error: dbError } = await supabase
        .from('journal_attachments')
        .delete()
        .eq('id', id);
      
      if (dbError) {
        toast.error(`Error deleting attachment record: ${dbError.message}`);
        return;
      }
      
      // Update local state
      setAttachments(prev => prev.filter(a => a.id !== id));
      
      // Invalidate the query to trigger a refetch
      await queryClient.invalidateQueries({ queryKey: ['journal-attachments', entryId] });
      
      toast.success('File deleted successfully');
    } catch (error: any) {
      console.error("Error deleting attachment:", error);
      toast.error(`Delete failed: ${error.message || 'Unknown error'}`);
    }
  };
  
  return {
    attachments,
    isLoading,
    isUploading,
    uploadAttachment,
    addAttachment,
    removeAttachment
  };
};
